import { useEffect, useState } from 'react';
import type { AdminApp, AdminStoreDecision, AdminStoreReviews, AdminStoreSubmission, StoreReadinessItem } from '@boardflight/shared';
import { STORE_READINESS_LABELS } from '@boardflight/shared';
import { post } from '../api.js';
import { Async, Panel, Time, useSelection } from '../ui.js';

/**
 * Store review (`docs/store-review-plan.md` §4) — the console's one screen with a queue on it.
 *
 * Every game whose team has asked to be on the store and that BoardFlight has never approved, as a
 * card with everything the decision is made in front of: the listing a stranger would read, the
 * artwork, and the build a stranger would be served. That build is already on the operator's own
 * Board — the `review` arm of `app_entitlements` puts it in *My Games* under the team's name for as
 * long as the review waits — so "play it" is a sentence here, not a button.
 *
 * Approve is permanent and is the last time this game comes through here: every build after it
 * ships without review. Request changes needs words, because the team reads them verbatim and they
 * are all it gets. Neither publishes or unpublishes anything; the team already pressed the button.
 *
 * `#reviews/<appId>` — the link in the submission email — scrolls to that game's card.
 */

/**
 * Where the public API lives, for the artwork and nothing else.
 *
 * `/v1/media/:id` is unauthenticated by design (the id is the capability, exactly as on a store
 * page), so the browser loads it straight from the API and this Worker still binds no bucket
 * (`docs/admin-plan.md` §D3). Compiled in, like the dashboard's URL in `App.tsx`.
 */
const API_URL = (import.meta.env.VITE_API_BASE_URL ?? 'https://api.boardflight.app').replace(
  /\/+$/,
  '',
);

const media = (id: string) => `${API_URL}/v1/media/${id}`;

export function Reviews({ state, reload }: {
  state: { data: AdminStoreReviews | null; error: string | null; loading: boolean };
  reload: () => void;
}) {
  const [selected] = useSelection('reviews');

  // After the cards render: the email's link names a game, and it should land on that game.
  useEffect(() => {
    if (!selected || !state.data) return;
    document.getElementById(`review-${selected}`)?.scrollIntoView({ block: 'start' });
  }, [selected, state.data]);

  return (
    <Async state={state}>
      {(data) => (
        <>
          <Panel title={`Waiting for review (${data.pending.length})`}>
            {data.pending.length === 0 ? (
              <p className="muted">
                Nothing is waiting. A game lands here the first time its owner presses Publish.
              </p>
            ) : (
              data.pending.map((submission) => (
                <Submission
                  key={submission.reviewId}
                  submission={submission}
                  focused={submission.app.id === selected}
                  earlier={data.decided.filter((row) => row.appId === submission.app.id && row.decision === 'rejected')}
                  onDecided={reload}
                />
              ))
            )}
          </Panel>

          <Panel title="Recent decisions">
            {data.decided.length === 0 ? (
              <p className="muted">None yet.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Game</th>
                    <th>Team</th>
                    <th>Decision</th>
                    <th>By</th>
                    <th>When</th>
                    <th className="num" title="From submitted to decided">Took</th>
                    <th>Note</th>
                    <th>Team emailed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.decided.map((row) => (
                    <tr key={row.reviewId}>
                      <td><a href={`#games/${row.appId}`}>{row.appName}</a></td>
                      <td>{row.teamName}</td>
                      <td>
                        <span
                          className={
                            row.decision === 'approved'
                              ? 'pill on'
                              : row.decision === 'rejected'
                                ? 'pill off'
                                : 'pill'
                          }
                        >
                          {row.decision === 'rejected' ? 'changes requested' : row.decision}
                        </span>
                      </td>
                      <td className="mono">{row.decidedBy ? `@${row.decidedBy}` : '—'}</td>
                      <td>
                        <Time at={row.decidedAt} />
                      </td>
                      <td className="num muted">{turnaround(row.decidedAt - row.submittedAt)}</td>
                      <td className="prose small">{row.note ?? <span className="muted">—</span>}</td>
                      <td className="muted small">
                        {/* Withdrawals are the team's own doing and are never emailed. */}
                        {row.decision === 'withdrawn' ? '—' : row.notified ? 'yes' : 'within the hour'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </>
      )}
    </Async>
  );
}

/** `40m`, `5h`, `3d`: how long a team waited, to the unit that matters. */
function turnaround(ms: number): string {
  const minutes = Math.max(0, Math.round(ms / 60_000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  return hours < 48 ? `${hours}h` : `${Math.round(hours / 24)}d`;
}

function Submission({ submission, focused, earlier, onDecided }: {
  submission: AdminStoreSubmission;
  focused: boolean;
  /** Changes requested of this game before, newest first: a resubmission is read against them. */
  earlier: AdminStoreDecision[];
  onDecided: () => void;
}) {
  const { app, latestPublicBuild: build } = submission;
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function decide(decision: 'approved' | 'rejected') {
    setError(null);
    setBusy(true);
    try {
      await post<AdminApp>(`/v1/admin/apps/${app.id}/store-review`, {
        decision,
        note: note.trim() || undefined,
      });
      setNote('');
      onDecided();
    } catch (failure) {
      setError(String(failure));
    } finally {
      setBusy(false);
    }
  }

  const players =
    submission.playersMin !== null && submission.playersMax !== null
      ? submission.playersMin === submission.playersMax
        ? `${submission.playersMin} player${submission.playersMin === 1 ? '' : 's'}`
        : `${submission.playersMin}–${submission.playersMax} players`
      : null;

  return (
    <article id={`review-${app.id}`} className={focused ? 'review focused' : 'review'}>
      <header className="review-head">
        {submission.iconMediaId ? (
          <img className="review-icon" src={media(submission.iconMediaId)} alt="" />
        ) : (
          <div className="review-icon" />
        )}
        <div>
          <h3>{app.name}</h3>
          <div className="muted small">
            {app.teamName} · {submission.submitter ? `@${submission.submitter}` : 'unknown'} ·
            submitted <Time at={submission.submittedAt} /> ·{' '}
            <span className="mono">{app.packageName}</span> · {app.platform}
          </div>
        </div>
      </header>

      {earlier.length > 0 ? (
        <div className="review-earlier">
          <strong>Resubmitted.</strong> Changes were asked for {earlier.length === 1 ? 'once' : `${earlier.length} times`};
          the last time, <Time at={earlier[0]!.decidedAt} />:
          <blockquote>{earlier[0]!.note ?? '(no note)'}</blockquote>
        </div>
      ) : null}

      {submission.tagline ? <p className="review-tagline">{submission.tagline}</p> : null}
      {submission.description ? <p className="prose review-description">{submission.description}</p> : null}

      <div className="row">
        {app.genres.map((genre) => (
          <span key={genre} className="pill">
            {genre}
          </span>
        ))}
        {players ? <span className="muted small">{players}</span> : null}
      </div>

      {submission.screenshotMediaIds.length > 0 ? (
        <div className="shots">
          {submission.screenshotMediaIds.map((id) => (
            <a key={id} href={media(id)} target="_blank" rel="noreferrer">
              <img src={media(id)} alt="" loading="lazy" />
            </a>
          ))}
        </div>
      ) : null}

      {/* Where the game is. This is the reason the review arm exists — the operator plays it on a
          Board, from the library they already have, with the button every tester presses. */}
      {build ? (
        <p className="note">
          <strong className="ok">On your Board:</strong> in <em>My Games</em>, under {app.teamName}.
          Version {build.versionName} ({build.versionCode}), shipped <Time at={build.createdAt} />
          {build.verifiedAt !== null ? ', verified on hardware' : ''}. Press Install.
        </p>
      ) : (
        <p className="note">
          <span className="warn">
            Nothing to play yet: no build on this game&rsquo;s <span className="mono">public</span>{' '}
            channel is one a stranger would be served
            {app.store.publicBuilds > 0 ? ' (they are waiting on hardware validation)' : ''}. It will
            appear in My Games on your Board, under {app.teamName}, when there is one. Approving now
            means the next build goes live unseen.
          </span>
        </p>
      )}

      {app.store.readinessWaivedAt !== null && app.store.missing.length > 0 ? (
        <p className="note">
          Submitted under a checklist waiver. Still missing:{' '}
          {app.store.missing
            .map((item) => STORE_READINESS_LABELS[item as StoreReadinessItem] ?? item)
            .join(' ')}
        </p>
      ) : null}

      <textarea
        className="review-note"
        rows={2}
        placeholder="Note to the team — required to request changes, optional to approve. They read it verbatim."
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="row">
        <button className="primary" disabled={busy} onClick={() => void decide('approved')}>
          {busy ? 'Saving…' : 'Approve for the library'}
        </button>
        <button
          className="danger"
          disabled={busy || note.trim() === ''}
          title={note.trim() === '' ? 'Say what needs to change first.' : undefined}
          onClick={() => void decide('rejected')}
        >
          Request changes
        </button>
        <span className="muted small">
          Approval is once per game: every build after it ships without review.
        </span>
      </div>
      {error ? <p className="error">{error}</p> : null}
    </article>
  );
}
